import { Box, Button, Stack, Typography } from '@mui/material'
import { format } from 'date-fns/esm'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'

import { selectCurrentPlanSnapshot } from '@/features/domain/history'
import { selectSchedulerResources } from '@/features/domain/scheduler'
import { selectCalendarRangeInterval } from '@/features/domain/ui'
import { useConvertDistance, useDateFnsOptions, useFormatCurrency, useFormatSeconds } from '@/hooks'
import {
  CalendarSchedule,
  CheckCircleOutlined,
  DeliveryTruck,
  Handshake,
  OrderMissing,
  PropertyManagement,
  RoutePin,
} from '@/icons'

import { useActions } from '../hooks/useActions'
import { useTexts } from '../useTexts'
import { StatCard } from './StatCard'

const STAT_ICON_SIZE = 48

export function CompleteScreen() {
  const texts = useTexts()
  const { viewRoutes } = useActions()
  const snapshot = useSelector(selectCurrentPlanSnapshot)
  const schedulerResources = useSelector(selectSchedulerResources)
  const { start, end } = useSelector(selectCalendarRangeInterval)

  const isMultiDay = start.getTime() !== end.getTime()

  // Design format: "Mar 1st" (single day) / "Mar 1st - Mar 15th" (range). The
  // locale options localize the month abbreviation and the ordinal day.
  const dateFnsOptions = useDateFnsOptions()
  const dateLabel = isMultiDay
    ? `${format(start, 'MMM do', dateFnsOptions)} - ${format(end, 'MMM do', dateFnsOptions)}`
    : format(start, 'MMM do', dateFnsOptions)

  const unassigned = snapshot?.order.unassigned.length ?? 0
  const orders = snapshot?.order.orders.length ?? 0
  // Vehicles used by the plan, not every territory vehicle. Scheduler resources are the routes in the
  // calendar range; a route is used when it has steps (same rule as the Routes panel "Used" counter).
  // Counted by vehicleId so a multi-day range reports distinct vehicles, not vehicle-days.
  const vehicles = useMemo(() => {
    const usedVehicleIds = new Set<string>()
    for (const resource of Object.values(schedulerResources)) {
      if (!resource.empty) usedVehicleIds.add(resource.vehicleId)
    }
    return usedVehicleIds.size
  }, [schedulerResources])
  // Costs are stored in cents
  const cost = (snapshot?.route.costs ?? 0) / 100
  const distanceMt = snapshot?.route.milage ?? 0
  const workingTimeSec = snapshot?.route.workingTime ?? 0

  // Locale/preference-aware formatting (currency, metric|imperial distance, duration)
  const costLabel = useFormatCurrency(cost)
  const distanceLabel = useConvertDistance(distanceMt)
  const workingTimeLabel = useFormatSeconds(workingTimeSec, 'EXTRA_SHORT')

  return (
    <Stack alignItems="center" sx={{ pt: '8px', pb: '32px' }}>
      <Box sx={{ mb: '24px' }}>
        <CheckCircleOutlined size={64} />
      </Box>

      <Typography variant="h6" sx={{ mb: '8px' }}>
        {texts.completeTitle}
      </Typography>

      <Typography variant="subtitle3" sx={{ mb: '8px', fontWeight: 500 }}>
        {dateLabel}
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: '24px', textAlign: 'center' }}>
        {texts.completeSubtitle}
      </Typography>

      <Stack direction="row" spacing={1} sx={{ width: '100%', mb: '24px' }}>
        <StatCard
          icon={<OrderMissing size={STAT_ICON_SIZE} />}
          value={String(unassigned)}
          label={isMultiDay ? texts.completeStatTotalUnassigned : texts.completeStatUnassigned}
        />
        <StatCard
          icon={<PropertyManagement size={STAT_ICON_SIZE} />}
          value={String(orders)}
          label={isMultiDay ? texts.completeStatTotalOrders : texts.completeStatOrders}
        />
        <StatCard
          icon={<DeliveryTruck size={STAT_ICON_SIZE} />}
          value={String(vehicles)}
          label={isMultiDay ? texts.completeStatTotalVehicles : texts.completeStatVehicles}
        />
      </Stack>

      <Stack direction="row" spacing={1} sx={{ width: '100%', mb: '40px' }}>
        <StatCard
          icon={<Handshake size={STAT_ICON_SIZE} />}
          value={costLabel}
          label={isMultiDay ? texts.completeStatTotalCost : texts.completeStatCost}
        />
        <StatCard
          icon={<RoutePin size={STAT_ICON_SIZE} />}
          value={distanceLabel}
          label={isMultiDay ? texts.completeStatTotalDistance : texts.completeStatDistance}
        />
        <StatCard
          icon={<CalendarSchedule size={STAT_ICON_SIZE} />}
          value={workingTimeLabel}
          label={isMultiDay ? texts.completeStatTotalWorkingTime : texts.completeStatWorkingTime}
        />
      </Stack>

      <Box>
        <Button
          variant="contained"
          onClick={viewRoutes}
          data-testid="build-progress__view-routes"
          sx={{ minWidth: 160 }}
        >
          {texts.completeViewRoutes}
        </Button>
      </Box>
    </Stack>
  )
}
